import { supabase } from "@/infrastructure/supabase/client";
import type { FeatureRow, FeatureStatus } from "@/infrastructure/supabase/database.types";

export interface FeatureFilters {
  status?: FeatureStatus;
  journey?: string;
  phaseId?: string;
}

export interface CreateFeatureInput {
  title: string;
  subtitle?: string | null;
  description?: string | null;
  domain?: FeatureRow["domain"];
  component_id?: string | null;
  journey?: string | null;
  notes?: string | null;
}

/** Coerce API rows so UI stays controlled (missing columns until migrations land). */
export function normalizeFeatureRow(row: FeatureRow): FeatureRow {
  return {
    ...row,
    subtitle: row.subtitle ?? null,
    review_acknowledged: row.review_acknowledged === true,
    notes: row.notes ?? null,
    domain: row.domain ?? null,
    component_id: row.component_id ?? null,
    triage_status: row.triage_status ?? null,
  };
}

function buildUpdatePayload(patch: Partial<FeatureRow>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(patch) as [
    keyof FeatureRow,
    FeatureRow[keyof FeatureRow],
  ][]) {
    if (value !== undefined) {
      out[key as string] = value;
    }
  }
  return out;
}

export const featuresRepository = {
  async list(filters: FeatureFilters = {}): Promise<FeatureRow[]> {
    let q = supabase.from("features").select("*").order("created_at", { ascending: false });
    if (filters.status) q = q.eq("status", filters.status);
    if (filters.journey) q = q.eq("journey", filters.journey);
    if (filters.phaseId) q = q.eq("phase_id", filters.phaseId);
    const { data, error } = await q;
    if (error) throw error;
    return (data as FeatureRow[] | null)?.map(normalizeFeatureRow) ?? [];
  },

  async byId(id: string): Promise<FeatureRow | null> {
    const { data, error } = await supabase.from("features").select("*").eq("id", id).maybeSingle();
    if (error) throw error;
    if (!data) return null;
    return normalizeFeatureRow(data as FeatureRow);
  },

  async create(input: CreateFeatureInput): Promise<FeatureRow> {
    const payload = {
      title: input.title,
      subtitle: input.subtitle ?? null,
      description: input.description ?? null,
      domain: input.domain ?? null,
      component_id: input.component_id ?? null,
      journey: input.journey ?? null,
      notes: input.notes ?? null,
      status: "open" as const,
    };
    const { data, error } = await supabase.from("features").insert(payload).select("*").single();
    if (error) throw error;
    return normalizeFeatureRow(data as FeatureRow);
  },

  async update(id: string, patch: Partial<FeatureRow>): Promise<FeatureRow> {
    const payload = buildUpdatePayload(patch);
    const { data, error } = await supabase
      .from("features")
      .update(payload)
      .eq("id", id)
      .select("*")
      .single();
    if (error) throw error;
    return normalizeFeatureRow(data as FeatureRow);
  },

  async delete(id: string): Promise<void> {
    // meeting_links rows that reference this feature are cleaned by the
    // database's ON DELETE CASCADE; if the cascade isn't in place, those rows
    // are orphaned but the polymorphic join already tolerates dangling ids.
    const { error } = await supabase.from("features").delete().eq("id", id);
    if (error) throw error;
  },

  subscribeToChanges(onChange: () => void) {
    const channel = supabase
      .channel("features-changes")
      .on("postgres_changes", { event: "*", schema: "public", table: "features" }, onChange)
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  },
};
